import { useState, useEffect } from 'react';
import { creatorAmount, isRealDonation } from '@/lib/donations/realDonation';
import { Search, Mail, Info } from 'lucide-react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useAuth } from '@/components/AuthProvider';
import { fetchDonorContacts } from '@/lib/firebase/data/donorContacts';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

type DonationFrequency = 'one_off' | 'monthly';

interface DonationRow {
    id: string;
    userId: string;
    creatorId: string;
    type: 'one_off' | 'monthly';
    amount: number;
    currency: string;
    status: 'pending' | 'succeeded' | 'failed' | 'processing' | 'active' | 'past_due' | 'canceled';
    createdAt: any;
    requestedEndDate?: any | null;
    stripePaymentIntentId?: string | null;
    stripeSubscriptionId?: string | null;
    userData?: { displayName: string; email: string };
}

export default function DonationsPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'all' | 'monthly' | 'oneoff_donors'>('all');
    const { user } = useAuth();
    const [rows, setRows] = useState<DonationRow[]>([]);
    const [payments, setPayments] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();
    const [userCache, setUserCache] = useState<Record<string, { displayName: string; email: string }>>({});
    const [missionPath, setMissionPath] = useState<string | undefined>(undefined);

    // Pre-fetched once on mount so "Copy page link" can call clipboard.writeText
    // synchronously from the click handler: an await beforehand (the old
    // per-click getDoc) breaks the user-activation chain on iOS Safari, so the
    // copy silently no-ops on mobile while it still "works" on desktop Chrome.
    // Ported from sportfund's 2ebea0a.
    useEffect(() => {
        if (!user?.uid) return;
        getDoc(doc(db, 'users', user.uid)).then(snap => {
            if (snap.exists()) setMissionPath((snap.data() as any).path);
        }).catch(() => { /* falls back to uid below */ });
    }, [user?.uid]);

    const mergeById = <T extends { id: string }>(...lists: T[][]): T[] => {
        const map = new Map<string, T>();
        lists.flat().forEach(item => {
            map.set(item.id, item);
        });
        return Array.from(map.values());
    };

    useEffect(() => {
        async function loadDonations() {
            if (!user?.uid) return;
            try {
                let donations: any[] = [];
                try {
                    const [snapshotCreator, snapshotRecipient] = await Promise.all([
                        getDocs(query(collection(db, 'donations'), where('creatorId', '==', user.uid))),
                        getDocs(query(collection(db, 'donations'), where('recipientId', '==', user.uid))),
                    ]);
                    donations = mergeById(
                        snapshotCreator.docs.map(d => ({ id: d.id, ...(d.data() as any) })),
                        snapshotRecipient.docs.map(d => ({ id: d.id, ...(d.data() as any) }))
                    );
                } catch (donErr) {
                    console.error('[DonationsPage] donations query error', donErr);
                }

                    // A row Stripe has never confirmed is not a donation. Until
                    // 2026-09-24 the checkout handler wrote `pending` BEFORE calling
                    // Stripe, so an abandoned form or a test script appeared here as
                    // if somebody had given -- one sat on the only athlete who has
                    // ever been paid. New rows are written after Stripe accepts the
                    // session; this covers the rows already in the database and every
                    // genuinely abandoned checkout.
                    donations = donations.filter(isRealDonation);

                try {
                    // One server-side call instead of one /users read per donation.
                    // The dashboard needs only displayName + email, and reading the
                    // whole donor document from the client is what forced the /users
                    // rule open to every signed-in account.
                    const donorContacts = await fetchDonorContacts(
                        donations.map((d) => d.userId).filter(Boolean)
                    );
                    const rowsWithUser = donations.map((don) => {
                        // A guest donation (TSF-158) has no account behind it, so
                        // there is no /users document. Dropping the row made the
                        // creator blind to money that had actually reached them.
                        const contact = don.userId ? donorContacts[don.userId] : undefined;
                        const base: DonationRow = {
                            id: don.id,
                            userId: don.userId || `guest:${don.donorEmail || don.id}`,
                            creatorId: don.creatorId || don.recipientId,
                            type: (don.frequency as DonationFrequency) === 'monthly' ? 'monthly' : 'one_off',
                            amount: creatorAmount(don),
                            currency: don.currency || 'usd',
                            status: don.status || 'pending',
                            createdAt: don.createdAt,
                            requestedEndDate: don.requestedEndDate || null,
                            stripePaymentIntentId: don.stripePaymentIntentId || null,
                            stripeSubscriptionId: don.stripeSubscriptionId || null,
                            userData: contact ? {
                                displayName: contact.displayName || 'Anonymous',
                                email: contact.email || 'No email'
                            } : (don.donorEmail ? { displayName: 'Guest', email: don.donorEmail } : undefined),
                        };
                        return base;
                    });
                    const valid = rowsWithUser.filter((r): r is DonationRow => r !== null);
                    setRows(valid);
                } catch (donRowErr) {
                    console.error('[DonationsPage] donations hydrate error', donRowErr);
                    setRows([]);
                }

                // Load payments for accurate, up-to-date totals and statuses
                let paymentsList: any[] = [];
                try {
                    const [paySnapCreator, paySnapRecipient] = await Promise.all([
                        getDocs(query(collection(db, 'payments'), where('creatorId', '==', user.uid))),
                        getDocs(query(collection(db, 'payments'), where('recipientId', '==', user.uid))),
                    ]);
                    paymentsList = mergeById(
                        paySnapCreator.docs.map(d => ({ id: d.id, ...(d.data() as any) })),
                        paySnapRecipient.docs.map(d => ({ id: d.id, ...(d.data() as any), creatorId: (d.data() as any).creatorId || (d.data() as any).recipientId }))
                    ).map(p => ({ ...p, creatorId: (p as any).creatorId || (p as any).recipientId }));
                    setPayments(paymentsList);
                } catch (payErr) {
                    console.error('[DonationsPage] payments query error', payErr);
                    setPayments([]);
                }

                // Warm user cache for payments' userIds
                try {
                    const uids = Array.from(new Set(paymentsList.map(p => p.userId).filter(Boolean)));
                    const newCache: Record<string, { displayName: string; email: string }> = { ...userCache };
                    const missing = uids.filter((uid) => !newCache[uid]);
                    // Payers can exist without a donation row, which is why the
                    // callable authorizes from payments as well — otherwise these
                    // names would silently come back blank.
                    const payerContacts = await fetchDonorContacts(missing);
                    for (const [uid, contact] of Object.entries(payerContacts)) {
                        newCache[uid] = {
                            displayName: contact.displayName || 'Anonymous',
                            email: contact.email || 'No email'
                        };
                    }
                    setUserCache(newCache);
                } catch (cacheErr) {
                    console.error('[DonationsPage] user cache warm error', cacheErr);
                }
            } finally {
                setIsLoading(false);
            }
        }

        loadDonations();
    }, [user?.uid]);

    // Build display lists
    const paymentDisplaySucceeded = payments.map(p => {
        const userInfo = userCache[p.userId]
            || (p.donorEmail ? { displayName: 'Guest', email: p.donorEmail } : { displayName: 'Anonymous', email: '' });
        const derivedType = p.type || (p.donationId ? (rows.find(r => r.id === p.donationId)?.type === 'monthly' ? 'monthly_cycle' : 'one_off') : undefined);
        return {
            id: p.id,
            name: userInfo.displayName,
            email: userInfo.email,
            type: derivedType === 'monthly_cycle' ? 'Monthly donation' : 'One-off payment',
            amount: creatorAmount(p),
            date: p.createdAt,
            status: p.status || 'succeeded'
        };
    });

    const paymentsByDonationId = new Set(payments.map(p => p.donationId).filter(Boolean));
    // Show donations confirmed by Stripe but without a payment record yet. This covers:
    //   - succeeded one-off donations (webhook updated donation but payment record missing)
    //   - active monthly subscriptions awaiting first invoice
    //   - processing bank-debit payments in transit
    // "pending" means checkout was opened — the donor may have abandoned it — never show those.
    const pendingDonationEntries = rows
        .filter(r => !paymentsByDonationId.has(r.id))
        .filter(r => {
            const isMonthly = r.type === 'monthly';
            if (isMonthly) {
                return r.status === 'active' || r.status === 'processing' || r.status === 'succeeded' || !!r.stripeSubscriptionId;
            }
            return r.status === 'succeeded' || r.status === 'processing';
        })
        .map(r => ({
            id: r.id,
            name: r.userData?.displayName || 'Anonymous',
            email: r.userData?.email || '',
            type: r.type === 'monthly' ? 'Monthly donation' : 'One-off payment',
            amount: r.amount || 0,
            date: r.createdAt,
            status: r.status || 'pending'
        }));

    const paymentDisplay = [...paymentDisplaySucceeded, ...pendingDonationEntries].filter(row => {
        const term = searchTerm.toLowerCase();
        return row.name.toLowerCase().includes(term) || row.email.toLowerCase().includes(term);
    });

    const filteredMonthlyDonations = rows.filter(r => r.type === 'monthly').filter(r => {
        const term = searchTerm.toLowerCase();
        const name = r.userData?.displayName || '';
        const email = r.userData?.email || '';
        return name.toLowerCase().includes(term) || email.toLowerCase().includes(term);
    });

    // Metrics calculated from payments (succeeded payments are the ground truth)
    const succeededPayments = payments.filter(p => p.status === 'succeeded');
    const donationIdToFreq: Record<string, 'one_off' | 'monthly'> = Object.fromEntries(rows.map(r => [r.id, r.type]));
    const succeededWithDerivedType = succeededPayments.map(p => ({
        ...p,
        derivedType: p.type || (p.donationId ? donationIdToFreq[p.donationId] === 'monthly' ? 'monthly_cycle' : 'one_off' : undefined)
    }));

    // Donor counts
    const totalDonors = new Set(rows.map(r => r.userId)).size;
    const monthlyRows = rows.filter(r => r.type === 'monthly');
    const monthlyDonorsTotal = new Set(monthlyRows.map(r => r.userId)).size;
    const now = new Date();
    const isMonthlyActive = (r: DonationRow) => {
        if (r.status === 'canceled') return false;
        if (r.status === 'active') return true;
        if (r.stripeSubscriptionId) return true; // subscription exists, treat as active pre-invoice
        if (r.requestedEndDate?.seconds) {
            // Consider active if end date in future
            return new Date(r.requestedEndDate.seconds * 1000) > now;
        }
        return false;
    };
    const monthlyDonorsActive = new Set(monthlyRows.filter(isMonthlyActive).map(r => r.userId)).size;
    const oneOffDonorsTotal = new Set(rows.filter(r => r.type === 'one_off').map(r => r.userId)).size;

    // Amounts
    const totalDonationsSucceeded = succeededWithDerivedType.reduce((sum, p) => sum + (p.amount || 0), 0);
    const totalOneOffSucceeded = succeededWithDerivedType.filter(p => p.derivedType === 'one_off').reduce((sum, p) => sum + (p.amount || 0), 0);
    const totalRecurringSucceeded = succeededWithDerivedType.filter(p => p.derivedType === 'monthly_cycle').reduce((sum, p) => sum + (p.amount || 0), 0);

    const pendingOrProcessingOneOff = rows
        .filter(r => r.type === 'one_off' && r.status === 'processing')
        .reduce((sum, r) => sum + (r.amount || 0), 0);
    // Monthly: treat rows with a subscription but no succeeded invoice yet as pending/in-transit
    const succeededDonationIds = new Set(
        succeededPayments.map(p => p.donationId).filter(Boolean)
    );
    const pendingOrProcessingMonthly = rows
        .filter(r => r.type === 'monthly' && !succeededDonationIds.has(r.id))
        .reduce((sum, r) => sum + (r.amount || 0), 0);
    const totalPendingOrProcessing = pendingOrProcessingOneOff + pendingOrProcessingMonthly;

    // Main numbers now include pending/in-transit
    const totalDonations = totalDonationsSucceeded + totalPendingOrProcessing;
    const totalOneOff = totalOneOffSucceeded + pendingOrProcessingOneOff;
    const totalRecurring = totalRecurringSucceeded + pendingOrProcessingMonthly;

    // For display: reconcile one-off status if payment succeeded
    const succeededPaymentIntentIds = new Set(
        succeededPayments.map(p => p.stripePaymentIntentId).filter(Boolean)
    );

    // One-off donors aggregation
    // Primary source: succeeded payments of derivedType 'one_off'
    const oneOffSucceededPayments = succeededWithDerivedType.filter(p => p.derivedType === 'one_off');
    const oneOffByUser: Record<string, { total: number; latestAmount: number; firstDate: any; lastDate: any; name: string; email: string }> = {};
    for (const p of oneOffSucceededPayments) {
        const uid = p.userId as string | undefined;
        if (!uid) continue;
        const created = p.createdAt;
        const userInfo = userCache[uid] || { displayName: 'Anonymous', email: '' };
        if (!oneOffByUser[uid]) {
            oneOffByUser[uid] = { total: p.amount || 0, latestAmount: p.amount || 0, firstDate: created, lastDate: created, name: userInfo.displayName, email: userInfo.email };
        } else {
            oneOffByUser[uid].total += p.amount || 0;
            const first = oneOffByUser[uid].firstDate;
            const last = oneOffByUser[uid].lastDate;
            oneOffByUser[uid].firstDate = (!first || (created?.seconds || 0) < (first?.seconds || 0)) ? created : first;
            if (!last || (created?.seconds || 0) > (last?.seconds || 0)) {
                oneOffByUser[uid].lastDate = created;
                oneOffByUser[uid].latestAmount = p.amount || 0;
            }
        }
    }
    // Include processing one-off donations (bank-debit in transit, no payment record yet)
    for (const r of rows.filter(r => r.type === 'one_off' && r.status === 'processing')) {
        const uid = r.userId as string | undefined;
        if (!uid) continue;
        const created = r.createdAt;
        const name = r.userData?.displayName || 'Anonymous';
        const email = r.userData?.email || '';
        if (!oneOffByUser[uid]) {
            oneOffByUser[uid] = { total: r.amount || 0, latestAmount: r.amount || 0, firstDate: created, lastDate: created, name, email };
        } else {
            oneOffByUser[uid].total += r.amount || 0;
            const first = oneOffByUser[uid].firstDate;
            const last = oneOffByUser[uid].lastDate;
            oneOffByUser[uid].firstDate = (!first || (created?.seconds || 0) < (first?.seconds || 0)) ? created : first;
            if (!last || (created?.seconds || 0) > (last?.seconds || 0)) {
                oneOffByUser[uid].lastDate = created;
                oneOffByUser[uid].latestAmount = r.amount || 0;
            }
        }
    }
    const oneOffDonorRows = Object.entries(oneOffByUser).map(([uid, info]) => ({
        userId: uid,
        name: info.name,
        email: info.email,
        total: info.total,
        latestAmount: info.latestAmount,
        firstDate: info.firstDate,
        lastDate: info.lastDate,
    })).filter(row => {
        const term = searchTerm.toLowerCase();
        return row.name.toLowerCase().includes(term) || row.email.toLowerCase().includes(term);
    });

    return (
        <div className="min-h-screen bg-white py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                    <h1 className="text-3xl font-bold">{t('donations.title')}</h1>
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder={t('audience.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* KPI cards */}
                {/* Removed full-width donors card */}

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                    {/* Donors first */}
                    <div className="bg-white p-4 rounded-lg border">
                        <div className="text-sm font-medium text-gray-500">Donors</div>
                        <div className="mt-2 space-y-1">
                            <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Total</span><span className="font-semibold text-gray-900">{totalDonors} total</span></div>
                            <div className="flex items-center justify-between text-sm"><span className="text-gray-500">Monthly (total / active)</span><span className="font-semibold text-gray-900">{monthlyDonorsTotal}/{monthlyDonorsActive}</span></div>
                            <div className="flex items-center justify-between text-sm"><span className="text-gray-500">One-off</span><span className="font-semibold text-gray-900">{oneOffDonorsTotal}</span></div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border">
                        <div className="text-sm font-medium text-gray-500">Total raised</div>
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(totalDonations)}</div>
                        <div className="text-xs text-gray-500 mt-1">Pending / in transit: {formatCurrency(totalPendingOrProcessing)}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border">
                        <div className="text-sm font-medium text-gray-500">One-off</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalOneOff)}</div>
                        <div className="text-xs text-gray-500 mt-1">Pending / in transit: {formatCurrency(pendingOrProcessingOneOff)}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border">
                        <div className="text-sm font-medium text-gray-500">Monthly</div>
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(totalRecurring)}</div>
                        <div className="text-xs text-gray-500 mt-1">Pending / in transit: {formatCurrency(pendingOrProcessingMonthly)}</div>
                    </div>
                </div>

                {/* Tabs: All payments / Monthly donors */}
                <div className="-mx-4 px-4 sm:mx-0 sm:px-0 border-b mb-6">
                    <nav className="flex gap-6 sm:gap-8 overflow-x-auto pb-1">
                        <button onClick={() => setActiveTab('all')} className={`whitespace-nowrap pb-4 px-1 border-b-2 transition-colors ${activeTab === 'all' ? 'text-primary border-primary' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>{t('donations.tabs.allPayments')}</button>
                        <button onClick={() => setActiveTab('monthly')} className={`whitespace-nowrap pb-4 px-1 border-b-2 transition-colors ${activeTab === 'monthly' ? 'text-primary border-primary' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>{t('donations.tabs.monthlyDonors')}</button>
                        <button onClick={() => setActiveTab('oneoff_donors')} className={`whitespace-nowrap pb-4 px-1 border-b-2 transition-colors ${activeTab === 'oneoff_donors' ? 'text-primary border-primary' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>One-off donors</button>
                    </nav>
                </div>

                {/* Table */}
                <div className="bg-white rounded-lg border overflow-hidden">
                    <div className={`hidden md:grid ${activeTab === 'all' ? 'grid-cols-6' : activeTab === 'monthly' ? 'grid-cols-6' : 'grid-cols-5'} gap-4 p-4 border-b text-xs font-semibold uppercase tracking-wide text-gray-500`}>
                        <div className={`${activeTab === 'oneoff_donors' ? 'col-span-1' : 'col-span-2'}`}>{t('audience.table.name')}</div>
                        {activeTab === 'all' && (
                            <>
                                <div className="col-span-1">{t('audience.table.type')}</div>
                                <div className="col-span-1">{t('audience.table.amount')}</div>
                                <div className="col-span-1">{t('donations.table.date')}</div>
                                <div className="col-span-1 flex items-center gap-2">{t('audience.table.status')}
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Info className="h-4 w-4 text-gray-400" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <div className="max-w-xs text-xs">
                                                    Pending: Payment created and awaiting bank confirmation. Card payouts typically complete within 2 business days; ACH/SEPA may take 3–7 business days.
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </>
                        )}
                        {activeTab === 'monthly' && (
                            <>
                                <div className="col-span-1">Status</div>
                                <div className="col-span-1">{t('audience.table.amount')}</div>
                                <div className="col-span-1">{t('donations.table.startDate')}</div>
                                <div className="col-span-1">{t('donations.table.endDate')}</div>
                            </>
                        )}
                        {activeTab === 'oneoff_donors' && (
                            <>
                                <div className="col-span-1">Amount (lifetime)</div>
                                <div className="col-span-1">Amount (latest)</div>
                                <div className="col-span-1">First donation</div>
                                <div className="col-span-1">Latest donation</div>
                            </>
                        )}
                    </div>
                    {isLoading ? (
                        <div className="p-12 text-center">
                            <div className="inline-flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                            </div>
                        </div>
                    ) : (activeTab === 'all' ? paymentDisplay.length === 0 : activeTab === 'monthly' ? filteredMonthlyDonations.length === 0 : oneOffDonorRows.length === 0) ? (
                        <div className="p-12">
                            <div className="max-w-2xl mx-auto text-center">
                                <div className="bg-gray-50 border border-dashed rounded-lg p-8">
                                    <h3 className="text-xl font-semibold mb-2">No donations yet</h3>
                                    <p className="text-gray-600 mb-6">This is where you’ll see an overview of your donations. You receive funds instantly to your bank account—Devotion Fund never holds your money. Share your page with friends and family to start raising support for your mission.</p>
                                    <div className="flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90"
                                            onClick={() => { window.location.href = '/creator/content/add'; }}
                                        >
                                            Create a post
                                        </button>
                                        <button
                                            type="button"
                                            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                                            onClick={async () => {
                                                const missionUrl = missionPath ? `${window.location.origin}/mission/${missionPath}` : `${window.location.origin}/mission/${user!.uid}`;
                                                try {
                                                    await navigator.clipboard.writeText(missionUrl);
                                                    toast.success(t('donations.linkCopied'));
                                                } catch {
                                                    toast.error(t('donations.linkCopyFailed'));
                                                }
                                            }}
                                        >
                                            Copy page link
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {activeTab === 'all' ? (
                                paymentDisplay.map((p) => (
                                    <div key={p.id} className="p-4 text-sm space-y-4 md:space-y-0 md:grid md:grid-cols-6 md:gap-4">
                                        <div className="md:col-span-2">
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('audience.table.name')}</div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium md:font-normal">{p.name}</span>
                                                {p.email && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { navigator.clipboard.writeText(p.email); toast.success(t('donations.emailCopied')); }}
                                                        className="p-1 rounded hover:bg-gray-100"
                                                        title={p.email}
                                                    >
                                                        <Mail className="h-4 w-4 text-gray-500" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('audience.table.type')}</div>
                                            {p.type}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('audience.table.amount')}</div>
                                            {formatCurrency(p.amount)}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('donations.table.date')}</div>
                                            {p.date?.seconds ? new Date(p.date.seconds * 1000).toLocaleDateString() : t('audience.table.none')}
                                        </div>
                                        <div className="capitalize">
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('audience.table.status')}</div>
                                            {(p.status === 'pending' || p.status === 'processing') ? (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="inline-flex items-center gap-1 cursor-default">
                                                                <span>{p.status}</span>
                                                                <Info className="h-4 w-4 text-gray-400" />
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-xs text-xs">
                                                            Pending: Payment created and awaiting bank confirmation. Card payouts typically complete within 2 business days; ACH/SEPA may take 3–7 business days.
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            ) : (
                                                p.status
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : activeTab === 'monthly' ? (
                                filteredMonthlyDonations.map((row) => (
                                    <div key={row.id} className="p-4 text-sm space-y-4 md:space-y-0 md:grid md:grid-cols-6 md:gap-4">
                                        <div className="md:col-span-2">
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('audience.table.name')}</div>
                                            <div className="flex items-center space-x-2">
                                                <span className="font-medium md:font-normal">{row.userData?.displayName}</span>
                                                {row.userData?.email && (
                                                    <button type="button" onClick={() => { navigator.clipboard.writeText(row.userData!.email); toast.success(t('donations.emailCopied')); }} className="p-1 rounded hover:bg-gray-100" title={row.userData.email}>
                                                        <Mail className="h-4 w-4 text-gray-500" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">Status</div>
                                            {isMonthlyActive(row) ? 'Active' : 'Ended'}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('audience.table.amount')}</div>
                                            {formatCurrency(row.amount || 0)}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('donations.table.startDate')}</div>
                                            {row.createdAt?.seconds ? new Date(row.createdAt.seconds * 1000).toLocaleDateString() : t('audience.table.none')}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('donations.table.endDate')}</div>
                                            {row.requestedEndDate?.seconds ? new Date(row.requestedEndDate.seconds * 1000).toLocaleDateString() : t('audience.table.none')}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                oneOffDonorRows.map((row) => (
                                    <div key={row.userId} className="p-4 text-sm space-y-4 md:space-y-0 md:grid md:grid-cols-5 md:gap-4">
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">{t('audience.table.name')}</div>
                                            <div className="flex items-center space-x-2">
                                                <span className="font-medium md:font-normal">{row.name}</span>
                                                {row.email && (
                                                    <button type="button" onClick={() => { navigator.clipboard.writeText(row.email); toast.success(t('donations.emailCopied')); }} className="p-1 rounded hover:bg-gray-100" title={row.email}>
                                                        <Mail className="h-4 w-4 text-gray-500" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">Amount (lifetime)</div>
                                            {formatCurrency(row.total)}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">Amount (latest)</div>
                                            {formatCurrency(row.latestAmount || 0)}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">First donation</div>
                                            {row.firstDate?.seconds ? new Date(row.firstDate.seconds * 1000).toLocaleDateString() : t('audience.table.none')}
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase text-gray-500 md:hidden">Latest donation</div>
                                            {row.lastDate?.seconds ? new Date(row.lastDate.seconds * 1000).toLocaleDateString() : t('audience.table.none')}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


